/**
 * THE ONE PLACE guest-facing deposit wording lives.
 *
 * Issue #480 · design §5.2 / §8.3 · **lead ruling R3 (2026-08-01, OPEN)**:
 * "hold vs charge" wording is Simon's decision and has NOT been made. Every
 * string a guest can read about a deposit is therefore centralised here so
 * applying that decision later is a one-file edit — the widget's payment step,
 * the flow actions, and the static return/cancel pages all read from this
 * module and never inline their own sentence.
 *
 * INTERIM WORDING RULE (binding until R3 lands): be honest about what actually
 * happens on the rail in question.
 *   - Only Stripe can place a genuine, refundable authorisation ("hold") on
 *     money. PayFast / Paystack / Square CANNOT: the deposit is a real CHARGE
 *     that is refunded on release. So no string here may describe *money* as
 *     being "held".
 *   - The server sends a `policyNote` on exactly the rails where that matters
 *     (§2.1). When present it is displayed verbatim — the server is the
 *     authority on what its gateway did. `moneyPolicyFallback` is only used
 *     when the server said nothing, and is deliberately silent about
 *     hold-vs-charge rather than guessing.
 *   - "Held" IS used about the TABLE (`tableHeldUntil`) — that is literally
 *     true: the seat is reserved `pending` until `expiresAt` (§7.2). The seat
 *     hold and the money are different things and the copy keeps them apart.
 *
 * The hold WINDOW (30 minutes, ruling R6) is a server-side constant and is
 * never restated here or anywhere else in the widget: the deadline the guest
 * sees is rendered from the `expiresAt` instant the server returned. There is
 * deliberately no `30` in this package.
 */

/** Format an ISO instant as a short local wall-clock time for guest copy. */
export function formatHoldDeadline(iso: string): string {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '';
  try {
    return new Date(t).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  } catch {
    return new Date(t).toISOString().slice(11, 16);
  }
}

export const DEPOSIT_COPY = {
  // ── Payment step ─────────────────────────────────────────────────────────
  /** Seat-hold deadline. About the TABLE, not the money (see the rule above). */
  tableHeldUntil: (deadline: string): string =>
    deadline
      ? `Your table is held until ${deadline}. Please complete payment before then.`
      : 'Your table is held while you complete payment.',
  /** Shown only when the server sent no `policyNote`. Says nothing about hold-vs-charge. */
  moneyPolicyFallback: 'A deposit is required to secure this booking.',
  hostedRedirectCta: 'Complete secure payment',
  hostedRedirectWaiting: 'Waiting for payment confirmation...',
  hostedRedirectReopen: 'Reopen payment page',

  // ── Failure copy (§5.2 — a deposit that cannot be taken STOPS the booking) ─
  errors: {
    slotTaken: 'Sorry — that time was just taken. Please pick another.',
    /**
     * 409 `online_capacity` (#504). NOT a slot-taken race: the party doesn't
     * fit the venue's online seat allowance for that time, so "pick another"
     * alone is bad advice for a party larger than the whole online capacity —
     * the phone is the real path. Same wording as the /book rail.
     */
    onlineCapacity:
      'No online availability for this time — please contact the restaurant directly.',
    /**
     * 409 `no_table_for_party` (#521). Distinct from onlineCapacity: the venue
     * may have plenty of seats free and simply nothing big enough for THIS
     * party. Retrying the same party at a quieter time can genuinely work
     * (a large table may be free later), so it stays retryable — but the copy
     * names the real constraint so a party larger than any table is sent to
     * the phone instead of hunting through every slot.
     */
    noTableForParty:
      'No table available for a party this size at that time — please try another time or contact the restaurant directly.',
    /**
     * 409 `replay_unresolved`. The idempotency key was claimed but the server
     * cannot address the booking behind it, so THIS ATTEMPT IS DEAD — telling
     * the guest to pick another time (the old bare-409 copy) is a lie that
     * sends them round the same loop. The widget starts a fresh attempt.
     */
    replayUnresolved:
      "We couldn't pick up your previous booking attempt, so nothing has been booked. Please try again — this starts a fresh booking.",
    /**
     * 409 `replay_lookup_failed`. A read the server could not complete — the
     * booking behind the key may well be fine. TRANSIENT: the same attempt is
     * retried under the SAME key so a double-submit still dedupes.
     */
    replayLookupFailed:
      "We couldn't check your booking just now, so nothing has been booked. Please try again in a moment.",
    /** 410 `checkout_no_longer_payable`. The seat hold lapsed — start over. */
    checkoutExpired:
      'That payment window has closed and your table was released, so nothing has been booked. Please try again to start a fresh booking.',
    depositRequired:
      "This venue requires a deposit for this booking, and we couldn't start the payment. Please try again.",
    gatewayUnreachable: "We couldn't reach the payment provider. Please try again in a moment.",
    generic:
      "We couldn't set up the deposit for this booking, so nothing has been booked. Please try again.",
    verifyFailed: 'Payment could not be confirmed. Please try again.',
    /** No held reservation to verify — never silently confirm a booking instead. */
    nothingToVerify:
      "We couldn't confirm this payment against your booking. Please contact the venue before paying again.",
  },

  // ── Static return / cancel pages (§8.3) ──────────────────────────────────
  return: {
    pageTitle: 'Payment — YeboGuest',
    confirmedTitle: 'Payment received — your booking is confirmed.',
    confirmedBody: 'You can close this window and return to your booking.',
    pendingTitle: "We're still confirming your payment.",
    pendingBody:
      "If it went through, the venue will confirm by email. Please don't pay again.",
    notFoundTitle: "We couldn't find this booking.",
    notFoundBody: 'Please check with the venue before paying again.',
    checkingTitle: 'Checking your payment…',
    checkingBody: 'This only takes a few seconds. Please keep this window open.',
    confirmationLabel: 'Confirmation number',
  },
  cancelled: {
    pageTitle: 'Payment cancelled — YeboGuest',
    title: "Payment cancelled — your table hasn't been booked.",
    body: 'Go back to your booking tab to try again, or contact the venue.',
    // #492 — the page now TELLS the server the guest gave up, so the table
    // frees immediately instead of blocking until the sweep times it out.
    releasedTitle: "Payment cancelled — your table hasn't been booked.",
    releasedBody:
      'The time slot has been released. Go back to your booking tab to start '
      + 'again, or contact the venue.',
    // The guest who paid and THEN landed here (back button, a second tab, an
    // ITN that won the race): the honest answer is that their booking exists.
    alreadySettledTitle: 'Good news — your payment actually went through.',
    alreadySettledBody:
      'Your booking is confirmed; there is nothing to cancel here. '
      + 'If you no longer want the booking, please contact the venue.',
  },
} as const;

// ── Failure classification (#480 G3 · the refusal-recovery contract) ─────────
//
// The server DISTINGUISHES its refusals by `code`; the widget must too. The old
// version switched on the bare STATUS, which produced the exact dead end this
// fix exists to remove: every 409 was read as "that time was just taken", so a
// `replay_unresolved` guest was told to pick another slot, resubmitted with the
// SAME idempotency key, hit the SAME claim, and got the SAME refusal — forever,
// with an in-memory store whose only escape was a page reload.
//
// Two axes, deliberately separate:
//
//  * WHAT THE GUEST IS TOLD  — must name the actual cause.
//  * WHETHER THE ATTEMPT IS DEAD — 'dead' means this idempotency key can never
//    succeed again, so the next submit MUST carry a fresh one. 'retryable'
//    means the same attempt is being retried and the key is KEPT: that is what
//    the key is for, and throwing it away on a transient failure is how a
//    double-submit turns into two bookings (and, on a claimed key, two seats).
//
// They live in one table on purpose — the classification is what selects the
// wording, and splitting them across two modules is how they drift apart.

/** Is this attempt finished, or is the same attempt being retried? */
export type DepositAttemptState = 'dead' | 'retryable';

export interface DepositFailure {
  /** Guest-facing sentence. */
  message: string;
  /**
   * 'dead'      ⇒ burn the idempotency key + any held-reservation handles; the
   *                next submit is a brand-new booking attempt.
   * 'retryable' ⇒ keep the key; the guest is retrying the SAME attempt and the
   *                server is expected to dedupe it.
   */
  attempt: DepositAttemptState;
  /** The server's `code`, when it sent one (diagnostics/tests). */
  code?: string;
}

/** Extract the server's machine-readable `code` from a thrown API error. */
export function depositErrorCode(err: unknown): string | undefined {
  const e = err as { code?: unknown; body?: unknown } | null;
  if (typeof e?.code === 'string' && e.code) return e.code;
  if (typeof e?.body !== 'string' || !e.body) return undefined;
  try {
    const parsed = JSON.parse(e.body) as { code?: unknown };
    return typeof parsed?.code === 'string' ? parsed.code : undefined;
  } catch {
    return undefined;
  }
}

/** code → (copy, attempt state). Everything not listed falls through to status. */
const BY_CODE: Readonly<Record<string, DepositFailure>> = {
  // 409s. Only the FIRST TWO of these are about the slot.
  slot_unavailable: { message: DEPOSIT_COPY.errors.slotTaken, attempt: 'retryable' },
  // #504 — capacity class, not a race: retryable (a quieter time may fit) but
  // the copy routes the guest to the restaurant for parties that never will.
  online_capacity: { message: DEPOSIT_COPY.errors.onlineCapacity, attempt: 'retryable' },
  // #521 — nothing on the floor plan fits this party then. Retryable (a bigger
  // table may free up at another time) with copy that names the actual reason.
  no_table_for_party: { message: DEPOSIT_COPY.errors.noTableForParty, attempt: 'retryable' },
  // #591 — over the venue's online party cap: terminal for the widget; the
  // reservationist is the path forward.
  party_too_large: { message: 'For a party this size, please contact the restaurant directly.', attempt: 'dead' },
  // The claim exists but the booking behind it does not. Same key ⇒ same
  // refusal, forever: the attempt is dead and the next submit starts fresh.
  replay_unresolved: { message: DEPOSIT_COPY.errors.replayUnresolved, attempt: 'dead' },
  // A read that THREW. The booking may be perfectly fine — keep the key so the
  // retry lands back on the SAME reservation instead of creating a second one.
  replay_lookup_failed: { message: DEPOSIT_COPY.errors.replayLookupFailed, attempt: 'retryable' },
  // 410s.
  checkout_no_longer_payable: { message: DEPOSIT_COPY.errors.checkoutExpired, attempt: 'dead' },
  // A stale cached bundle hitting the retired /deposit/charge route. Nothing
  // was claimed; a reload is the real fix, but a fresh attempt is honest.
  deposit_charge_retired: { message: DEPOSIT_COPY.errors.generic, attempt: 'dead' },
  // 502s. Nothing the guest can fix, and nothing that a new key improves.
  gateway_no_handle: { message: DEPOSIT_COPY.errors.gatewayUnreachable, attempt: 'retryable' },
  gateway_error: { message: DEPOSIT_COPY.errors.gatewayUnreachable, attempt: 'retryable' },
  // The intent existed but the row write failed. The server says nothing was
  // recorded — but the KEY may already be claimed, and the widget cannot prove
  // which. Keeping it is the safe half: the next attempt replays deterministically
  // (into `replay_unresolved`, which then burns it) rather than risking a second
  // seat. Never mint a fresh key on a "we are not sure what got written".
  deposit_not_recorded: { message: DEPOSIT_COPY.errors.generic, attempt: 'retryable' },
};

/** status → (copy, attempt state) for a refusal that carried no `code`. */
function byStatus(status: number | undefined): DepositFailure {
  switch (status) {
    case 409:
      // A code-less 409 is a pre-#480 server (or a proxy-mangled body): the only
      // 409 that shape ever meant was capacity. Retryable — the capacity
      // pre-check runs BEFORE the idempotency claim, so the key is still unused.
      return { message: DEPOSIT_COPY.errors.slotTaken, attempt: 'retryable' };
    case 410:
      // Gone, cause unknown. Whatever this key addressed is not coming back.
      return { message: DEPOSIT_COPY.errors.checkoutExpired, attempt: 'dead' };
    case 402:
      return { message: DEPOSIT_COPY.errors.depositRequired, attempt: 'retryable' };
    case 502:
    case 503:
    case 504:
      return { message: DEPOSIT_COPY.errors.gatewayUnreachable, attempt: 'retryable' };
    default:
      // Includes a network error (no status at all) — the request may have been
      // received, so the key is KEPT and the retry dedupes.
      return { message: DEPOSIT_COPY.errors.generic, attempt: 'retryable' };
  }
}

/**
 * Classify a failed `/deposit/checkout` (or `/deposit/verify`) call: what to
 * tell the guest, and whether the booking attempt behind it is dead.
 *
 * Every branch STOPS the booking — there is deliberately no outcome here that
 * implies the guest may proceed without paying — but every branch is also
 * RECOVERABLE from inside the widget, without a page reload.
 */
export function classifyDepositFailure(err: unknown): DepositFailure {
  const code = depositErrorCode(err);
  const byCode = code ? BY_CODE[code] : undefined;
  if (byCode) return { ...byCode, code };
  const status = (err as { status?: number } | null)?.status;
  return { ...byStatus(status), ...(code ? { code } : {}) };
}

/** Guest-facing sentence for a failed deposit call. */
export function depositErrorCopy(err: unknown): string {
  return classifyDepositFailure(err).message;
}
